// Lightweight client-side analytics helpers. Safe to call even when the
// underlying scripts are not loaded (e.g. when env vars are unset in dev) —
// calls silently no-op so callers never need to guard.

type GtagFn = (...args: unknown[]) => void
type FbqFn = (...args: unknown[]) => void

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    gtag?: GtagFn
    dataLayer?: unknown[]
    fbq?: FbqFn
  }
}

const GOOGLE_ADS_CONVERSION_ID = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID || ""
const GOOGLE_ADS_PURCHASE_LABEL = process.env.REACT_APP_GOOGLE_ADS_PURCHASE_LABEL || ""

export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return
  try {
    window.gtag("event", name, params || {})
  } catch {
    // never let analytics break a UI flow
  }
}

export type OnboardingStep = "password" | "measures" | "setup" | "success"

export function trackOnboardingStep(step: OnboardingStep): void {
  trackEvent("onboarding_step", { step })
}

/**
 * Fires the Google Ads Purchase conversion. gclid auto-attribution handles the
 * click-side; this tag is what closes the loop so Smart Bidding can optimize.
 */
export function trackGoogleAdsPurchase(params: {
  transactionId: string
  value?: number
  currency?: string
}): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return
  if (!GOOGLE_ADS_CONVERSION_ID || !GOOGLE_ADS_PURCHASE_LABEL) return
  try {
    window.gtag("event", "conversion", {
      send_to: `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_PURCHASE_LABEL}`,
      transaction_id: params.transactionId,
      ...(typeof params.value === "number" ? { value: params.value } : {}),
      ...(params.currency ? { currency: params.currency } : {}),
    })
  } catch {
    // ignore
  }
}

/**
 * Fires the Meta Pixel Purchase. eventID must match the Stripe session id used
 * by the server-side CAPI call so Meta dedupes the two into one conversion.
 */
export function trackMetaPixelPurchase(params: {
  eventId: string
  value?: number
  currency?: string
}): void {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return
  try {
    window.fbq(
      "track",
      "Purchase",
      {
        ...(typeof params.value === "number" ? { value: params.value } : {}),
        ...(params.currency ? { currency: params.currency } : {}),
      },
      { eventID: params.eventId },
    )
  } catch {
    // ignore
  }
}
